import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Send, Mail, Lock, User, AlertCircle } from 'lucide-react';

const BOT_USERNAME =
  (import.meta.env.VITE_BOT_USERNAME as string | undefined) ??
  'neuromaster_agent_bot';

type AuthTab = 'telegram' | 'email';
type EmailMode = 'signin' | 'signup';

export default function Login() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // Deep-link support: `/login?signup=1` or `?mode=email|signup` open the
  // email tab immediately so the "Регистрация" button in the header can land
  // the user on the right form without an extra click.
  const wantsSignup =
    searchParams.get('signup') === '1' ||
    searchParams.get('mode') === 'signup';
  const wantsEmailTab = wantsSignup || searchParams.get('mode') === 'email';
  const [tab, setTab] = useState<AuthTab>(wantsEmailTab ? 'email' : 'telegram');

  return (
    <div className="min-h-[100dvh] bg-[#0B0E14] text-[#F0F2F5] flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-50 pointer-events-none" />
      <div
        className="absolute top-[10%] left-[10%] w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, rgba(0,212,255,0.1) 0%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />

      <div className="relative z-10 max-w-md w-full">
        <Link
          to="/"
          className="text-[#8A93A7] text-xs hover:text-[#F0F2F5] transition-colors mb-8 inline-block"
        >
          ← {t('auth.backToHome')}
        </Link>

        <div className="glass-card rounded-2xl p-7 sm:p-8">
          {/* Tab switcher */}
          <div className="flex gap-1 p-1 mb-7 rounded-xl bg-[rgba(11,14,20,0.6)]">
            <TabButton
              active={tab === 'telegram'}
              onClick={() => setTab('telegram')}
              icon={<Send size={13} />}
              label="Telegram"
            />
            <TabButton
              active={tab === 'email'}
              onClick={() => setTab('email')}
              icon={<Mail size={13} />}
              label="Email"
            />
          </div>

          {tab === 'telegram' ? (
            <TelegramTab />
          ) : (
            <EmailTab initialMode={wantsSignup ? 'signup' : 'signin'} />
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-1.5 h-10 rounded-lg text-[13px] font-semibold transition-all
        ${active
          ? 'bg-[#00D4FF] text-[#0B0E14] shadow-md shadow-[rgba(0,212,255,0.25)]'
          : 'text-[#B5BBC9] hover:text-[#F0F2F5]'}`}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── Telegram tab — Login Widget redirect flow ──────────────────────────────

function TelegramTab() {
  const { t } = useTranslation();
  const widgetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = widgetRef.current;
    if (!target) return;
    target.replaceChildren();

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', BOT_USERNAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-userpic', 'true');
    script.setAttribute('data-radius', '12');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute(
      'data-auth-url',
      `${window.location.origin}/api/auth/telegram?next=/account`
    );
    target.appendChild(script);

    return () => {
      target.replaceChildren();
    };
  }, []);

  return (
    <div className="text-center">
      <div
        className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center"
        style={{
          background:
            'linear-gradient(135deg, #229ED9 0%, #2AABEE 100%)',
          boxShadow: '0 8px 28px rgba(34,158,217,0.35)',
        }}
      >
        <Send size={22} className="text-white" />
      </div>

      <h1 className="text-[#F0F2F5] text-xl font-bold mb-1.5">
        {t('auth.signIn')}
      </h1>
      <p className="text-[#B5BBC9] text-[13px] mb-6 leading-relaxed">
        {t('auth.signInHint')}
      </p>

      <div ref={widgetRef} className="flex justify-center mb-2 min-h-[44px]" />

      <p className="text-[#8A93A7] text-[10.5px] mt-5 leading-relaxed">
        {t('auth.signInPrivacy')}
      </p>
    </div>
  );
}

// ─── Email tab — sign in / sign up form ─────────────────────────────────────

function EmailTab({ initialMode = 'signin' }: { initialMode?: EmailMode }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<EmailMode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    setError(null);
    try {
      const endpoint =
        mode === 'signup' ? '/api/auth/signup' : '/api/auth/email-login';
      const body =
        mode === 'signup'
          ? { email, password, firstName: firstName || undefined }
          : { email, password };
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        navigate('/account', { replace: true });
        return;
      }
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.status === 409) {
        setError(
          'Этот email уже зарегистрирован. Войдите или используйте другой адрес.'
        );
      } else if (res.status === 401) {
        setError('Неверный email или пароль.');
      } else if (res.status === 400) {
        setError(data.error ?? 'Проверьте email и пароль (минимум 8 символов).');
      } else {
        setError(data.error ?? `HTTP ${res.status}`);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div
        className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center"
        style={{
          background:
            'linear-gradient(135deg, rgba(0,212,255,0.2) 0%, rgba(0,212,255,0.05) 100%)',
          boxShadow: '0 8px 28px rgba(0,212,255,0.20)',
          color: '#00D4FF',
        }}
      >
        <Mail size={22} />
      </div>

      <h1 className="text-[#F0F2F5] text-xl font-bold mb-1.5 text-center">
        {mode === 'signup' ? 'Регистрация' : 'Вход по email'}
      </h1>
      <p className="text-[#B5BBC9] text-[13px] mb-6 leading-relaxed text-center">
        {mode === 'signup'
          ? 'Создайте аккаунт — заявки и платежи будут привязаны к вашему профилю.'
          : 'Войдите по email и паролю.'}
      </p>

      <form onSubmit={submit} className="space-y-3">
        {mode === 'signup' && (
          <div>
            <label className="text-[#8A93A7] text-[10px] uppercase tracking-wider font-medium mb-1.5 block">
              Имя
            </label>
            <div className="relative">
              <User
                size={14}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8A93A7] pointer-events-none"
              />
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                autoComplete="given-name"
                placeholder="Иван"
                maxLength={64}
                className="w-full h-11 glass-input rounded-xl pl-9 pr-4 text-[#F0F2F5] text-sm placeholder:text-[#8A93A7]/50"
                disabled={busy}
              />
            </div>
          </div>
        )}

        <div>
          <label className="text-[#8A93A7] text-[10px] uppercase tracking-wider font-medium mb-1.5 block">
            Email
          </label>
          <div className="relative">
            <Mail
              size={14}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8A93A7] pointer-events-none"
            />
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              required
              placeholder="you@example.com"
              className="w-full h-11 glass-input rounded-xl pl-9 pr-4 text-[#F0F2F5] text-sm placeholder:text-[#8A93A7]/50"
              disabled={busy}
            />
          </div>
        </div>

        <div>
          <label className="text-[#8A93A7] text-[10px] uppercase tracking-wider font-medium mb-1.5 block">
            Пароль {mode === 'signup' && '(мин. 8 символов)'}
          </label>
          <div className="relative">
            <Lock
              size={14}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8A93A7] pointer-events-none"
            />
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
              minLength={mode === 'signup' ? 8 : undefined}
              placeholder="••••••••"
              className="w-full h-11 glass-input rounded-xl pl-9 pr-4 text-[#F0F2F5] text-sm placeholder:text-[#8A93A7]/50"
              disabled={busy}
            />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-[rgba(255,71,87,0.08)] border border-[rgba(255,71,87,0.25)]">
            <AlertCircle
              size={14}
              className="text-[#FF4757] flex-shrink-0 mt-0.5"
            />
            <p className="text-[#FF4757] text-[12px]">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !email || !password}
          className="w-full h-11 glass-btn text-[#0B0E14] font-bold text-sm rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? 'Подождите…' : mode === 'signup' ? 'Зарегистрироваться' : 'Войти'}
        </button>
      </form>

      <p className="text-[#8A93A7] text-[12px] text-center mt-5">
        {mode === 'signup' ? (
          <>
            Уже есть аккаунт?{' '}
            <button
              type="button"
              onClick={() => {
                setMode('signin');
                setError(null);
              }}
              className="text-[#00D4FF] hover:underline"
            >
              Войти
            </button>
          </>
        ) : (
          <>
            Нет аккаунта?{' '}
            <button
              type="button"
              onClick={() => {
                setMode('signup');
                setError(null);
              }}
              className="text-[#00D4FF] hover:underline"
            >
              Зарегистрироваться
            </button>
          </>
        )}
      </p>
    </div>
  );
}
